import { useLazyQuery } from '@apollo/client';
import { useCallback, useState, useMemo, useEffect } from 'react';

import { GetOrdersQuery } from '../data/queries/getOrdersQuery.gql';
import {
  formatOrdersResponse,
  getQueryVariables,
  DEFAULT_PAGE_SIZE,
} from '../data/utils/orderUtils';
import type { OrderWithCustomProducts } from '../data/utils/types';

// Type for the query response - we'll infer it from the query
type GetOrdersQueryResponse = {
  searchOrder?:
    | {
        __typename: 'SearchOrderConnection';
        edges: Array<{ node: any; cursor: string }>;
        pageInfo: {
          hasNextPage: boolean;
          hasPreviousPage: boolean;
          startCursor: string | null;
          endCursor: string | null;
        };
        total?: number | null;
      }
    | {
        __typename: 'InputValidationFailure';
        message: string;
      };
};

export type SearchOrdersParams = {
  searchTerm?: string;
  page?: number;
  pageSize?: number;
  filter?: {
    status?: string;
    paymentStatus?: string;
    buyerId?: string;
    supplierId?: string;
  };
};

export type UseSearchOrdersResponse = {
  orders: OrderWithCustomProducts[];
  totalOrders: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  hasNextPage: boolean;
  currentPage: number;
  totalPages: number;
  searchOrders: (params: SearchOrdersParams) => Promise<void>;
  refetch: () => Promise<void>;
};

export type UseSearchOrdersConfig = {
  initialPageSize: number;
  captureException?: (error: Error) => void;
};

export const useSearchOrders = (
  config: UseSearchOrdersConfig
): UseSearchOrdersResponse => {
  const { initialPageSize = DEFAULT_PAGE_SIZE, captureException } = config;
  const [lastParams, setLastParams] = useState<SearchOrdersParams | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [executeQuery, { data, loading, error }] =
    useLazyQuery<GetOrdersQueryResponse>(GetOrdersQuery, {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    });

  // Handle errors using useEffect instead of onError callback
  useEffect(() => {
    if (error && captureException) {
      captureException(error);
    }
  }, [error, captureException]);

  const searchOrders = useCallback(
    async (params: SearchOrdersParams) => {
      try {
        setLastParams(params);
        const page = params.page || 1;
        setCurrentPage(page);
        const pageSize = params.pageSize || initialPageSize;

        const variables = getQueryVariables({
          searchTerm: params.searchTerm,
          supplierRefId: params.filter?.supplierId,
          pageSize,
        });

        await executeQuery({
          variables,
        });
      } catch (err) {
        const caughtError = err as Error;
        captureException?.(caughtError);
        throw caughtError;
      }
    },
    [executeQuery, initialPageSize, captureException]
  );

  const refetch = useCallback(async () => {
    if (lastParams) {
      await searchOrders(lastParams);
    }
  }, [lastParams, searchOrders]);

  const { orders, pageInfo } = useMemo(
    () => formatOrdersResponse(data),
    [data]
  );

  // Extract total from searchOrder response if available
  const totalOrders = useMemo(() => {
    const searchOrder = data?.searchOrder;
    if (searchOrder && '__typename' in searchOrder) {
      if (searchOrder.__typename === 'SearchOrderConnection') {
        return (searchOrder as any).total || 0;
      }
    }
    return 0;
  }, [data?.searchOrder]);

  const totalPages = useMemo(() => {
    if (!totalOrders || !lastParams?.pageSize) return 0;
    const pageSize = lastParams.pageSize || initialPageSize;
    return Math.ceil(totalOrders / pageSize);
  }, [totalOrders, lastParams?.pageSize, initialPageSize]);

  const hasNextPage = useMemo(() => {
    return pageInfo.hasNextPage || currentPage < totalPages;
  }, [pageInfo.hasNextPage, currentPage, totalPages]);

  return useMemo(
    () => ({
      orders: orders || [],
      totalOrders,
      isLoading: loading,
      isFetching: loading,
      error: error || null,
      hasNextPage,
      currentPage,
      totalPages,
      searchOrders,
      refetch,
    }),
    [
      orders,
      totalOrders,
      loading,
      error,
      hasNextPage,
      currentPage,
      totalPages,
      searchOrders,
      refetch,
    ]
  );
};
